import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DisclaimerPhoneValidation } from '@/components/Elements/DisclaimerPhoneValidation';
import { Footer } from '@/components/Elements/Footer';
import {
  FormPhoneValidation,
  iFormPhoneValidationFields,
} from '@/components/Form/FormPhoneValidation';
import { FormTokenCheck, iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { MobileToken } from '@/features/mobileToken';
import { UserData } from '@/types';
import { isNewFreeUser } from '@/utils/helpers';

export type PhoneValidationContainerProps = {
  className?: string;
  mobileToken: MobileToken;
  userData: UserData;
  showFooter?: boolean;
  onRequestInfo?: () => void;
  onSubmitFormPhoneValidation: (values: iFormPhoneValidationFields) => void;
  onSubmitFormTokenCheck: (values: iFormTokenCheckFields) => void;
};

export const PhoneValidationContainer = ({
  className,
  mobileToken,
  userData,
  showFooter = false,
  onRequestInfo,
  onSubmitFormPhoneValidation,
  onSubmitFormTokenCheck,
}: PhoneValidationContainerProps) => {
  const { t } = useTranslation();

  const getShowFormPhoneValidation = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (
      isNewFreeUser(userData) &&
      (typeof mobileToken.deliveries === 'undefined' || parseInt(mobileToken.deliveries, 10) < 3) &&
      (typeof mobileToken.validAt === 'undefined' || !mobileToken.validAt) &&
      (typeof mobileToken.usedAt === 'undefined' || !mobileToken.usedAt)
    ) {
      return true;
    }

    return false;
  };

  const getShowFormTokenCheck = (mobileToken: MobileToken, userData: UserData): boolean => {
    if (isNewFreeUser(userData) && mobileToken.validAt) {
      return true;
    }
    return false;
  };

  const [showFormPhoneValidation, setShowFormPhoneValidation] = useState(
    getShowFormPhoneValidation(mobileToken, userData)
  );
  const [showFormTokenCheck, setShowFormTokenCheck] = useState(
    getShowFormTokenCheck(mobileToken, userData)
  );

  const onDefaultSubmitFormPhoneValidation = async (values: iFormPhoneValidationFields) => {
    onSubmitFormPhoneValidation && (await onSubmitFormPhoneValidation(values));

    setShowFormPhoneValidation(false);
    setShowFormTokenCheck(true);
  };

  const onDefaultSubmitFormTokenCheck = (values: iFormTokenCheckFields) => {
    onSubmitFormTokenCheck && onSubmitFormTokenCheck(values);

    setShowFormPhoneValidation(getShowFormPhoneValidation(mobileToken, userData));
    setShowFormTokenCheck(getShowFormTokenCheck(mobileToken, userData));
  };

  const onRetryDelivery = () => {
    setShowFormPhoneValidation(true);
    setShowFormTokenCheck(false);
  };

  const getFormPhoneValidation = () => {
    return (
      <FormPhoneValidation
        className={className}
        initialValues={mobileToken}
        onSubmit={onDefaultSubmitFormPhoneValidation}
        mobileToken={mobileToken}
        userData={userData}
      />
    );
  };

  const getFormTokenCheck = () => {
    return (
      <FormTokenCheck
        className={className}
        initialValues={mobileToken}
        onSubmit={onDefaultSubmitFormTokenCheck}
        onRetryDelivery={onRetryDelivery}
        mobileToken={mobileToken}
        userData={userData}
      />
    );
  };

  return (
    <div>
      {showFormPhoneValidation && getFormPhoneValidation()}
      {showFormTokenCheck && getFormTokenCheck()}
      {!showFormPhoneValidation && !showFormTokenCheck && (
        <h3 className="md__payments__container--h3">{t('Forms.tokenChecked')}</h3>
      )}
      <DisclaimerPhoneValidation />
      {showFooter && onRequestInfo && <Footer onRequestInfo={onRequestInfo} />}
    </div>
  );
};
